/*
 * Description:
 * ---------------------
 * Cloud Anchor panel with interactive SVG world map.
 * Anchors are plotted at their AWS region coordinates.
 * Click a pin to switch the active tunnel endpoint.
 *
 * A gateway is named by where it is ("N. Virginia", "Frankfurt 2"), never by
 * its address, and each tile leads with what the last check found and when.
 * The list is the box's own: nothing here adds a gateway.
 *
 * ! file CloudPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  FiMapPin, FiRefreshCw, FiCheck, FiAlertCircle, FiGlobe, FiTarget, FiActivity,
} from "react-icons/fi";
import WORLD_PATHS from "./worldMapPaths";
import { useConfirm } from "./ConfirmModal";
import { fetchWithTimeout, isAbortError } from "../utils/fetchWithTimeout";
import { ago, fmtDuration } from "../utils/formatters";

// A switch waits up to 2.5 s on the edge for the gateway to answer before it
// moves anything, so the request needs longer than the default before it is
// worth giving up on.
const SWITCH_TIMEOUT_MS = 6000;

// How long to keep asking the edge which gateway is active before calling the
// switch failed. Covers the window where it is re-registering its api_url.
const SWITCH_VERIFY_ATTEMPTS = 4;
const SWITCH_VERIFY_DELAY_MS = 750;

// A measurement round is one probe burst plus one timeout per anchor, run
// concurrently, so it lands in a couple of seconds — but a slow uplink and a
// dead anchor both push against that, and the answer is worth waiting for.
const SELECTION_RUN_TIMEOUT_MS = 15000;

// Cadences offered for automatic re-checking.
//
// Anchor quality moves with routing changes rather than with seconds, so the
// operational range starts at minutes. The sub-minute end is for watching the
// mechanism work — a demo, or proving a link change is picked up — and is
// marked as such rather than presented as an equal choice.
const FAST_INTERVAL_SEC = 30;

// How far ahead a gateway must be before the tunnel moves, on the same 0-100
// scale the WAN health ranking uses. Two gateways inside this of each other are
// treated as equivalent and nothing moves, which is the whole defence against
// trading places on noise.
const SELECTION_MARGINS = [
  { label: "5 pts (eager)", value: 5 },
  { label: "8 pts", value: 8 },
  { label: "15 pts (reluctant)", value: 15 },
  { label: "25 pts (only if dramatic)", value: 25 },
];
const SELECTION_INTERVALS = [
  { label: "500 ms", value: 0.5 },
  { label: "1 sec", value: 1 },
  { label: "2 sec", value: 2 },
  { label: "5 sec", value: 5 },
  { label: "10 sec", value: 10 },
  { label: "30 sec", value: 30 },
  { label: "1 min", value: 60 },
  { label: "5 min", value: 300 },
  { label: "15 min", value: 900 },
  { label: "1 hour", value: 3600 },
];

// How long a failed gateway is tolerated, in words. The dial sets a cadence;
// this is the consequence the operator is actually choosing, so it is shown
// whether or not automatic re-checking is on — leaving a broken gateway happens
// in both modes.
const fmtEscape = (sec) => {
  if (sec == null) return null;
  return sec < 1 ? `${Math.round(sec * 1000)} ms` : `${Number(sec.toFixed(1))} s`;
};

// The edge reports -1 for "nothing scheduled", which is no time at all.
const countdown = (sec) => (sec == null || sec < 0 ? null : fmtDuration(sec));

const fmtMs = (ms) => (ms < 10 ? `${ms.toFixed(1)} ms` : `${Math.round(ms)} ms`);

/* ═══════════════════════════════════════════════════════════════════════════
   REGION DATA — IP prefix → AWS region → map coordinates + label
   ═══════════════════════════════════════════════════════════════════════════ */

// Keyed on the CIDR block the anchor's public IP falls in.
//
// CIDR rather than a fixed two-octet key because AWS does not hand out regions
// on /16 boundaries. 18.89.0.0/16 is two different places — the top half is a
// Houston local zone, the bottom half is Frankfurt — so any scheme that can
// only express a /16 has to file one of them under the wrong continent.
const REGION_HINTS = {
  "44.196.0.0/16": "us-east-1",
  "18.214.0.0/16": "us-east-1",
  "18.234.0.0/16": "us-east-1",
  "52.200.0.0/13": "us-east-1",
  "3.14.0.0/15":   "us-east-2",   // Ohio
  "52.60.0.0/16":  "ca-central-1",
  "18.135.0.0/16": "eu-west-2",
  "63.186.0.0/16": "eu-central-1",
  "3.120.0.0/14":  "eu-central-1",  // a /14 in AWS's own ip-ranges.json
  "13.229.0.0/16": "ap-southeast-1",
  "52.52.0.0/16":  "us-west-1",
  "54.241.0.0/16": "us-west-1",   // N. California
  "13.52.0.0/16":  "us-west-1",
  "54.207.0.0/16": "sa-east-1",
  // Houston is a LOCAL ZONE, not a region — us-east-1-iah-2 hangs off
  // us-east-1. It gets its own entry rather than folding into us-east-1
  // because the whole point of this map is where the traffic actually
  // surfaces, and Houston is ~2000 km from N. Virginia.
  "18.89.128.0/18": "us-east-1-iah-2",
  "18.89.0.0/18":   "eu-central-1",
};

const ipToInt = (addr) => {
  const octets = String(addr).split(".");
  if (octets.length !== 4) return null;
  let n = 0;
  for (const octet of octets) {
    // Number("") is 0 and Number("1e2") is 100 — neither is an octet.
    if (!/^\d{1,3}$/.test(octet)) return null;
    const v = Number(octet);
    if (v > 255) return null;
    n = n * 256 + v;
  }
  return n;
};

// Longest prefix first, so a /18 carved out of a /16 wins over the /16 that
// contains it. Sorting by block size ascending is the same ordering.
//
// Division rather than bitwise masking: JS bitwise operators coerce to *signed*
// 32-bit, so any address at or above 128.0.0.0 comes out negative and compares
// wrong. Nothing here is hot enough to care about the difference.
const HINT_TABLE = Object.entries(REGION_HINTS)
  .map(([cidr, region]) => {
    const [network, bits] = cidr.split("/");
    const size = 2 ** (32 - Number(bits));
    return { block: Math.floor(ipToInt(network) / size), size, region };
  })
  .sort((a, b) => a.size - b.size);

const guessRegion = (addr) => {
  if (!addr) return null;
  const ip = ipToInt(addr);
  if (ip === null) return null;
  const hit = HINT_TABLE.find((h) => Math.floor(ip / h.size) === h.block);
  return hit ? hit.region : null;
};

// A gateway named by host name is placed by the address the box resolved it
// to, which /cloud/anchors reports with the data port.
const regionOf = (anchor) => guessRegion(anchor.host) || guessRegion((anchor.addr || "").split(":")[0]);

// Mercator-ish x/y for SVG viewBox 0 0 900 460
// Each region gets a coordinate and a friendly label
// Equirectangular projection: x = (lon+180)*900/360, y = (90-lat)*460/180
const REGION_COORDS = {
  "us-east-1":      { x: 256, y: 130, label: "N. Virginia",    flag: "🇺🇸" },
  "us-east-1-iah-2": { x: 212, y: 154, label: "Houston",       flag: "🇺🇸" },
  "us-east-2":      { x: 243, y: 128, label: "Ohio",           flag: "🇺🇸" },
  "us-west-1":      { x: 145, y: 135, label: "N. California",  flag: "🇺🇸" },
  "us-west-2":      { x: 151, y: 114, label: "Oregon",         flag: "🇺🇸" },
  "ca-central-1":   { x: 266, y: 114, label: "Canada",         flag: "🇨🇦" },
  "eu-west-1":      { x: 434, y:  94, label: "Ireland",        flag: "🇮🇪" },
  "eu-west-2":      { x: 450, y:  98, label: "London",         flag: "🇬🇧" },
  "eu-west-3":      { x: 456, y: 105, label: "Paris",          flag: "🇫🇷" },
  "eu-central-1":   { x: 472, y: 102, label: "Frankfurt",      flag: "🇩🇪" },
  "eu-north-1":     { x: 495, y:  78, label: "Stockholm",      flag: "🇸🇪" },
  "ap-southeast-1": { x: 710, y: 227, label: "Singapore",      flag: "🇸🇬" },
  "ap-southeast-2": { x: 828, y: 317, label: "Sydney",         flag: "🇦🇺" },
  "ap-northeast-1": { x: 799, y: 139, label: "Tokyo",          flag: "🇯🇵" },
  "ap-northeast-2": { x: 768, y: 134, label: "Seoul",          flag: "🇰🇷" },
  "ap-south-1":     { x: 632, y: 181, label: "Mumbai",         flag: "🇮🇳" },
  "sa-east-1":      { x: 333, y: 290, label: "São Paulo",      flag: "🇧🇷" },
  "me-south-1":     { x: 576, y: 163, label: "Bahrain",        flag: "🇧🇭" },
  "af-south-1":     { x: 496, y: 317, label: "Cape Town",      flag: "🇿🇦" },
};

// Fallback: distribute unknowns in a circle around center
// A gateway whose location cannot be worked out from its address is kept in a
// tray in the map's empty south-west corner, never drawn at a guessed place.
const UNKNOWN_COORDS = { x: 70, y: 418, label: "Location not known", flag: "🌐" };

/* What each gateway is called here, by host: the place it is in, numbered when
   a place holds several ("Frankfurt 2"), else its place in the box's list
   ("Gateway 3"). Numbered in the order the box lists them, a list that only
   grows, so a name stays put from one poll to the next. */
export function gatewayNames(anchors) {
  const inRegion = {};
  anchors.forEach((a) => {
    const r = regionOf(a);
    if (r && REGION_COORDS[r]) (inRegion[r] ||= []).push(a.host);
  });
  const names = {};
  anchors.forEach((a, i) => {
    const r = regionOf(a);
    const peers = inRegion[r];
    if (!peers) names[a.host] = `Gateway ${i + 1}`;
    else names[a.host] = peers.length > 1 ? `${REGION_COORDS[r].label} ${peers.indexOf(a.host) + 1}` : REGION_COORDS[r].label;
  });
  return names;
}

/* The gateway's health as its tile leads with it, from the last check. An
   anchor the check did not measure has no health to show, which is not the
   same claim as one that did not answer. */
function healthOf(result) {
  if (!result) return { kind: "unknown", words: "Not checked yet" };
  if (!result.reachable) return { kind: "down", words: "Not answering" };
  return { kind: "up", words: `Answering, ${fmtMs(result.rtt_ms)}` };
}

/* ═══════════════════════════════════════════════════════════════════════════
   SVG WORLD MAP OUTLINE (simplified, tasteful)
   ═══════════════════════════════════════════════════════════════════════════ */

// Natural Earth 110m land polygons — public domain, production grade
const WorldMapPaths = React.memo(() => (
  <g className="cloud-map-continents" opacity="0.08" fill="currentColor" stroke="currentColor" strokeWidth="0.3" strokeLinejoin="round">
    {WORLD_PATHS.map((d, i) => (
      <path key={i} d={d} />
    ))}
  </g>
));

/* ═══════════════════════════════════════════════════════════════════════════
   ANIMATED CONNECTION LINE
   ═══════════════════════════════════════════════════════════════════════════ */

function ConnectionLine({ from, to, isActive }) {
  // Quadratic bezier via midpoint raised
  const mx = (from.x + to.x) / 2;
  const my = Math.min(from.y, to.y) - 30;
  const d = `M${from.x},${from.y} Q${mx},${my} ${to.x},${to.y}`;

  return (
    <g>
      <path
        d={d}
        fill="none"
        stroke={isActive ? "#6366F1" : "#94A3B8"}
        strokeWidth={isActive ? 2 : 1}
        strokeDasharray={isActive ? "none" : "4 4"}
        opacity={isActive ? 0.7 : 0.25}
        className={isActive ? "cloud-line-active" : ""}
      />
      {isActive && (
        <circle r="3" fill="#6366F1">
          <animateMotion dur="2s" repeatCount="indefinite" path={d} />
        </circle>
      )}
    </g>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ANCHOR PIN
   ═══════════════════════════════════════════════════════════════════════════ */

// The pin of the gateway in use does nothing when clicked, so only the others
// take the pointer.
function AnchorPin({ anchor, coord, isActive, isSwitching, onClick, onHover, onLeave }) {
  const { x, y } = coord;
  return (
    <g
      className={`cloud-pin ${isActive ? "cloud-pin--active" : "cloud-pin--idle"}`}
      style={isActive ? undefined : { cursor: "pointer" }}
      onClick={isActive ? undefined : () => onClick(anchor)}
      onMouseEnter={() => onHover(anchor)}
      onMouseLeave={onLeave}
    >
      {/* Pulse ring for active */}
      {isActive && (
        <circle cx={x} cy={y} r="14" fill="none" stroke="#6366F1" strokeWidth="2" opacity="0.3">
          <animate attributeName="r" values="14;22;14" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0.05;0.3" dur="2s" repeatCount="indefinite" />
        </circle>
      )}
      {/* Pin body */}
      <circle
        cx={x}
        cy={y}
        r={isActive ? 10 : 7}
        fill={isActive ? "#6366F1" : "#E2E8F0"}
        stroke={isActive ? "#4F46E5" : "#94A3B8"}
        strokeWidth={isActive ? 2.5 : 1.5}
        className="cloud-pin-dot"
      />
      {/* Inner dot */}
      <circle cx={x} cy={y} r={isActive ? 3.5 : 2.5} fill="#fff" />
      {/* Switching spinner */}
      {isSwitching && (
        <g transform={`translate(${x}, ${y})`}>
          <circle r="15" fill="none" stroke="#F59E0B" strokeWidth="2" strokeDasharray="8 8" opacity="0.8">
            <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="1s" repeatCount="indefinite" />
          </circle>
        </g>
      )}
    </g>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TOOLTIP
   ═══════════════════════════════════════════════════════════════════════════ */

function MapTooltip({ anchor, name, coord, region, measure }) {
  if (!anchor || !coord) return null;
  const meta = REGION_COORDS[region] || UNKNOWN_COORDS;
  const tx = Math.min(coord.x + 16, 800);
  const ty = coord.y - 10;
  const health = healthOf(measure);

  return (
    <foreignObject x={tx} y={ty - 50} width="200" height="100" style={{ pointerEvents: "none" }}>
      <div className="cloud-map-tooltip">
        <div className="cloud-map-tooltip-region">
          {meta.flag} {name}
          {region && <span className="cloud-map-tooltip-code">{region}</span>}
        </div>
        <div className={`cloud-map-tooltip-health cloud-map-tooltip-health--${health.kind}`}>{health.words}</div>
        {anchor.active && <div className="cloud-map-tooltip-active">● In use by this box</div>}
        {!anchor.active && <div className="cloud-map-tooltip-hint">Click to switch to it</div>}
      </div>
    </foreignObject>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   AUTOMATIC GATEWAY SELECTION
   ═══════════════════════════════════════════════════════════════════════════ */

// The one line that explains itself: what the last check found, and — when the
// edge has seen a better gateway and not moved to it — why not. "b is better"
// with no answer to "so why am I still on a" is the version that generates
// support tickets.
function selectionNarrative(sel, nameOf) {
  if (!sel) return "";
  // A verification run pauses re-checking and leaves the mode as the operator
  // set it, so the toggle stays on and this line says why no check is due.
  if (sel.suspended && sel.mode === "auto") {
    return "Re-checking is paused while a verification run is in progress, and resumes when it ends. A gateway that stops answering is still left.";
  }
  const last = sel.last;
  const measured = last?.anchors?.length || 0;

  if (!last) return "No check has run yet.";
  if (!last.best) return "No gateway answered the last check.";

  const active = nameOf(sel.active);
  const best = nameOf(last.best);
  if (sel.mode !== "auto") {
    if (last.best === sel.active) {
      return `${active} was the best of ${measured} measured, and is held until this box restarts.`;
    }
    // The gap the operator has to decide about. The edge will not act on it in
    // this mode, so say so rather than leaving them to infer it.
    return `${best} now rates higher than ${active}. Held until restart: turn on re-checking, or switch by hand.`;
  }

  // A hold after a recent switch outranks everything else worth saying: the
  // edge has decided, and is deliberately not acting yet.
  if (sel.dwell_remaining_sec > 0) {
    const who = sel.candidate ? `${nameOf(sel.candidate)} is ahead, but ` : "";
    return `${who}holding for ${countdown(sel.dwell_remaining_sec)} after the last switch, so two close gateways cannot trade places.`;
  }
  if (last.best === sel.active) return `${active} is the best of ${measured} measured.`;
  if (sel.candidate) {
    const need = sel.confirmations - sel.streak;
    return `${nameOf(sel.candidate)} is ahead — switching after ${need} more agreeing check${need === 1 ? "" : "s"}.`;
  }
  return `${best} rated highest, but not by the ${sel.margin}-point margin needed to move.`;
}

/* One gateway, led by its health in large type: what the last check found,
   and how long ago, since a check can be hours old and an old "not answering"
   is not a live one. Only its switch is a control. */
function GatewayTile({ anchor, name, region, result, checkedAt, best, switching, onSwitch }) {
  const health = healthOf(result);
  const meta = region ? REGION_COORDS[region] : UNKNOWN_COORDS;
  const detail = [
    region || "location not known",
    result?.reachable && `± ${fmtMs(result.jitter_ms)} jitter`,
    result?.reachable && (result.loss > 0 ? `${Math.round(result.loss * 100)}% loss` : "no loss"),
  ].filter(Boolean).join(" · ");

  return (
    <div className={`cloud-gw${anchor.active ? " cloud-gw--active" : ""}`} role="listitem" aria-label={name}>
      <div className="cloud-gw-top">
        <div className={`cloud-gw-health cloud-gw-health--${health.kind}`}>
          <span className="cloud-gw-dot" aria-hidden="true" />
          {health.words}
        </div>
        {anchor.active && <span className="cloud-gw-inuse"><FiCheck size={13} /> In use</span>}
      </div>
      {result && checkedAt && <div className="cloud-gw-checked">Checked {ago(checkedAt)}</div>}

      <div className="cloud-gw-name">
        <span className="cloud-gw-flag" aria-hidden="true">{meta.flag}</span>
        {name}
        {best && <span className="cloud-gw-best">best in the last check</span>}
      </div>
      <div className="cloud-gw-detail">{detail}</div>

      <div className="cloud-gw-foot">
        {anchor.active ? (
          <span className="cloud-gw-using">This box's traffic goes through it.</span>
        ) : (
          <button type="button" className="cloud-gw-switch" disabled={!!switching} onClick={() => onSwitch(anchor)}>
            {switching === anchor.host ? <FiRefreshCw size={13} className="cloud-spin" /> : null}
            {switching === anchor.host ? "Checking it answers…" : "Switch to it"}
          </button>
        )}
      </div>
    </div>
  );
}

function SelectionBar({ selection, busy, nameOf, onMode, onInterval, onMargin, onRunNow }) {
  const auto = selection?.mode === "auto";
  const next = auto ? countdown(selection?.next_check_sec) : null;
  const running = busy || selection?.running;

  return (
    <div className="cloud-sel">
      <div className="cloud-sel-main">
        <div className="cloud-sel-title">
          <FiTarget size={15} />
          <span>Gateway selection</span>
        </div>

        {/* Every edge already measures its gateways at boot and comes up on the
            best one. This toggle is only about whether it keeps checking. */}
        <button
          type="button"
          role="switch"
          aria-checked={auto}
          className={`cloud-sel-switch ${auto ? "cloud-sel-switch--on" : ""}`}
          disabled={busy}
          onClick={() => onMode(auto ? "static" : "auto")}
          title={
            auto
              ? "Stop re-checking. The current gateway is held until this edge restarts."
              : "Keep measuring on a cadence and move if another gateway becomes clearly better."
          }
        >
          <span className="cloud-sel-switch-track"><span className="cloud-sel-switch-knob" /></span>
          Re-check automatically
        </button>

        <label className="cloud-sel-every">
            {auto ? "every" : "watch every"}
            <select
              value={selection.interval_sec}
              disabled={busy}
              onChange={(e) => onInterval(Number(e.target.value))}
              title={
                auto
                  ? "How often gateways are re-measured, and how closely the active one is watched for failure."
                  : "How closely the active gateway is watched for failure. Nothing moves the tunnel on this setting alone unless the gateway stops working."
              }
            >
              {SELECTION_INTERVALS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
              {/* An interval set outside this panel stays visible instead of
                  being silently snapped to one of the presets. */}
              {!SELECTION_INTERVALS.some((o) => o.value === selection.interval_sec) && (
                <option value={selection.interval_sec}>{countdown(selection.interval_sec)}</option>
              )}
            </select>
        </label>

        {/* Below a couple of seconds a round takes longer than the gap between
            them, so the dial stops describing anything. Say so where it is set
            rather than leaving it to be discovered. */}
        {auto && selection.interval_sec < FAST_INTERVAL_SEC && (
          <span className="cloud-sel-fast" title="Rounds run back to back at this rate. Useful for watching a change get picked up, not for leaving on.">
            diagnostic rate
          </span>
        )}

        {auto && (
          <label className="cloud-sel-every">
            if better by
            <select
              value={selection.margin}
              disabled={busy}
              onChange={(e) => onMargin(Number(e.target.value))}
              title="Gateways within this of each other count as equivalent, and the tunnel stays where it is."
            >
              {SELECTION_MARGINS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
              {!SELECTION_MARGINS.some((o) => o.value === selection.margin) && (
                <option value={selection.margin}>{selection.margin} pts</option>
              )}
            </select>
          </label>
        )}

        {next && <span className="cloud-sel-next">next in {next}</span>}

        <button
          type="button"
          className="cloud-sel-run"
          disabled={running}
          onClick={onRunNow}
          title="Measure latency, jitter and loss to every gateway now"
        >
          {running ? <FiRefreshCw size={13} className="cloud-spin" /> : <FiActivity size={13} />}
          {running ? "Checking…" : "Check now"}
        </button>
      </div>

      <div className="cloud-sel-note">{selectionNarrative(selection, nameOf)}</div>

      {/* The dial's actual consequence. An operator who picks 500 ms is buying a
          detection time, not a cadence, and if the health prober's own interval
          is the thing holding it back they need to be told which panel to go to
          rather than left wondering why the number did not move. */}
      {selection.escape && (
        <div className="cloud-sel-note">
          A failed gateway is left after {fmtEscape(selection.escape.silent_sec)} of silence,
          or {fmtEscape(selection.escape.stalled_sec)} of answering without delivering.
          {selection.escape.limited_by === "probe" && (
            <> Limited by the health probe interval ({fmtEscape(selection.escape.evidence_sec)}), not by this dial.</>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

export default function CloudPanel({ nodeApiUrl, selectedNode }) {
  const confirm = useConfirm();
  const [anchors, setAnchors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [switching, setSwitching] = useState(false);
  const [switchTarget, setSwitchTarget] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [switchResult, setSwitchResult] = useState(null);
  const [hovered, setHovered] = useState(null);
  // null while unknown, false once the edge has said it does not serve
  // selection at all (an older build, or direct mode). The two are different
  // answers and the panel must not render the control until it knows which.
  const [selection, setSelection] = useState(null);
  const [selSupported, setSelSupported] = useState(null);
  const [selBusy, setSelBusy] = useState(false);
  const containerRef = useRef(null);

  // ── Fetch ──

  // Returns the anchor list, or null if it could not be read. The switch
  // handler needs the answer as a value, not only as state, because it decides
  // success from what the edge reports rather than from its own request.
  const fetchAnchors = useCallback(async (signal) => {
    const url = nodeApiUrl("/cloud/anchors");
    if (!url) return null;
    try {
      const opts = signal ? { signal } : {};
      const res = await fetchWithTimeout(url, opts);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      if (!signal?.aborted) {
        setAnchors(list);
        setError("");
        setLastFetch(new Date());
      }
      return list;
    } catch (err) {
      if (err.name === "AbortError") return null;
      console.error("[CloudPanel] fetch error:", err);
      if (!signal?.aborted) setError(err.message || "Failed to load cloud gateways");
      return null;
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [nodeApiUrl]);

  // Selection status is a read of state the edge already holds — it never
  // triggers a measurement — so it polls beside the anchor list.
  const fetchSelection = useCallback(async (signal) => {
    const url = nodeApiUrl("/cloud/selection");
    if (!url) return null;
    try {
      const opts = signal ? { signal } : {};
      const res = await fetchWithTimeout(url, opts);
      if (res.status === 404 || res.status === 503) {
        if (!signal?.aborted) setSelSupported(false);
        return null;
      }
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = await res.json();
      if (!signal?.aborted) { setSelection(data); setSelSupported(true); }
      return data;
    } catch (err) {
      if (err.name === "AbortError") return null;
      // A transient read failure is not evidence the feature is missing, so
      // whatever the panel already knows about support is left standing.
      console.info("[CloudPanel] selection read:", err.message);
      return null;
    }
  }, [nodeApiUrl]);

  useEffect(() => {
    if (!selectedNode) { setAnchors([]); setLoading(false); return; }
    const controller = new AbortController();
    setAnchors([]); setLoading(true);
    setSelection(null); setSelSupported(null);
    fetchAnchors(controller.signal);
    fetchSelection(controller.signal);
    const timer = setInterval(() => {
      fetchAnchors(controller.signal);
      fetchSelection(controller.signal);
    }, 5000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [selectedNode, fetchAnchors, fetchSelection]);

  // A switch that worked is shown briefly. One that did not stays until the
  // next try: kept apart from `error`, which every successful poll clears.
  useEffect(() => {
    if (!switchResult?.ok) return;
    const timer = setTimeout(() => setSwitchResult(null), 6000);
    return () => clearTimeout(timer);
  }, [switchResult]);

  // ── Switch handler ──

  const names = useMemo(() => gatewayNames(anchors), [anchors]);
  const nameOf = useCallback((host) => names[host] || "a gateway", [names]);

  const handleSwitch = async (anchor) => {
    const host = anchor.host;
    if (anchor.active || switching) return;
    const to = nameOf(host);
    const from = anchors.find((a) => a.active);
    const ok = await confirm({
      title: `Switch to ${to}?`,
      message:
        "New connections go through it. Connections already open stay on the current gateway for up to " +
        "10 minutes. The box first checks that this gateway answers, and stays where it is if it does not. " +
        "Choosing a gateway by hand stops automatic re-checking, so it will not be moved back.",
      confirmLabel: "Switch",
      variant: "primary",
    });
    if (!ok) return;

    setSwitching(true);
    setSwitchTarget(host);
    setSwitchResult(null);
    setError("");
    const stayed = from ? nameOf(from.host) : "the gateway it was on";

    // The outcome is read back from the edge, not taken from this request's
    // response, because the switch moves the tunnel that the response has to
    // travel through. The reply is routinely lost and the proxy reports 502 on
    // a switch that worked — and a duplicate request used to come back "already
    // on <host>", which is the desired end state described as a failure.
    //
    // So: fire the request, then ask the edge which gateway is actually
    // active. That answer is the one the operator sees. The one reply believed
    // as it stands is a refusal: nothing moved, so it came back the way it went.
    let refused = false;
    try {
      const url = nodeApiUrl("/cloud/switch");
      if (!url) throw new Error("No node selected");
      const res = await fetchWithTimeout(
        url,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ host }),
        },
        SWITCH_TIMEOUT_MS,
      );
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        refused = body.code === "unanswered";
      } else if (!res.ok) {
        console.info("[CloudPanel] switch request:", res.status, await res.text());
      }
    } catch (err) {
      // A lost or timed-out response says nothing about the switch itself.
      if (!isAbortError(err)) console.info("[CloudPanel] switch request:", err.message);
    }

    if (refused) {
      setSwitchResult({ ok: false, message: `${to} did not answer on any internet line, so this box stayed on ${stayed}.` });
      fetchAnchors();
      setSwitching(false);
      setSwitchTarget(null);
      return;
    }

    // The edge re-registers its api_url as it switches, so the first read or
    // two can fail while that propagates. One miss is not a failed switch.
    let active = false;
    let latest = null;
    for (let attempt = 0; attempt < SWITCH_VERIFY_ATTEMPTS && !active; attempt++) {
      await new Promise((r) => setTimeout(r, SWITCH_VERIFY_DELAY_MS));
      const list = await fetchAnchors();
      if (Array.isArray(list)) latest = list;
      active = Array.isArray(list) && list.some((a) => a.host === host && a.active);
    }

    if (active) {
      setError("");
      setSwitchResult({ ok: true, name: to });
      // The switch pinned this gateway on the edge, so the panel's own copy of
      // the policy is stale the moment it succeeds.
      fetchSelection();
    } else {
      // What the request itself said is not shown: a reply lost on the way
      // back reads as an error on a switch that worked, and the edge's words
      // carry the gateway's address.
      const now = latest?.find((a) => a.active);
      setSwitchResult({
        ok: false,
        message: !now || now.host === from?.host
          ? `${to} did not become active, so this box stayed on ${stayed}.`
          : `${to} did not become active. This box is on ${nameOf(now.host)}.`,
      });
    }

    setSwitching(false);
    setSwitchTarget(null);
  };

  // ── Selection handlers ──

  // Sends only the fields being changed; the edge overlays them onto the
  // current policy, so a mode toggle cannot silently reset the cadence.
  const updateSelection = async (patch) => {
    try {
      setSelBusy(true);
      setError("");
      const url = nodeApiUrl("/cloud/selection");
      if (!url) throw new Error("No node selected");
      const res = await fetchWithTimeout(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed (${res.status})`);
      }
      setSelection(await res.json());
    } catch (err) {
      console.error("[CloudPanel] selection update:", err);
      setError(err.message || "Failed to update gateway selection");
    } finally {
      setSelBusy(false);
    }
  };

  // Measures every gateway now. In auto mode this can move the tunnel, so the
  // anchor list is re-read afterwards rather than trusted to the poll.
  const runSelectionNow = async () => {
    try {
      setSelBusy(true);
      setError("");
      const url = nodeApiUrl("/cloud/selection/run");
      if (!url) throw new Error("No node selected");
      const res = await fetchWithTimeout(url, { method: "POST" }, SELECTION_RUN_TIMEOUT_MS);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // A failed round still carries its measurement — showing which gateway
        // won beside the reason the move failed beats showing nothing.
        if (body.status) setSelection(body.status);
        throw new Error(body.error || `Check failed (${res.status})`);
      }
      setSelection(body);
      await fetchAnchors();
    } catch (err) {
      console.error("[CloudPanel] selection run:", err);
      setError(isAbortError(err) ? "Gateway check timed out" : err.message || "Gateway check failed");
    } finally {
      setSelBusy(false);
    }
  };

  // ── Coordinate mapping ──

  const anchorCoords = useMemo(() => {
    let unknownIdx = 0;

    // How many anchors share each region, and which index this one is. Two
    // anchors in the same region resolve to identical coordinates, so without
    // this they render exactly on top of each other and look like one pin.
    const regionCounts = {};
    anchors.forEach((a) => {
      const r = regionOf(a);
      if (r) regionCounts[r] = (regionCounts[r] || 0) + 1;
    });
    const regionSeen = {};

    return anchors.map((a) => {
      const region = regionOf(a);
      const base = region ? REGION_COORDS[region] : null;
      if (base) {
        const total = regionCounts[region];
        const idx = regionSeen[region] = (regionSeen[region] || 0);
        regionSeen[region] += 1;
        if (total <= 1) return { anchor: a, region, coord: base, siblings: 1, sibIndex: 0 };
        // Fan them on a small arc centred on the region, so the cluster still
        // reads as "these are all here" rather than scattering across the map.
        const spread = Math.min(9 + total * 2, 18);
        const angle = (-90 + (idx - (total - 1) / 2) * (140 / total)) * Math.PI / 180;
        return {
          anchor: a,
          region,
          coord: {
            ...base,
            x: base.x + Math.cos(angle) * spread,
            y: base.y + Math.sin(angle) * spread * 0.6,
          },
          siblings: total,
          sibIndex: idx,
        };
      }
      const x = UNKNOWN_COORDS.x + unknownIdx * 34;
      unknownIdx++;
      return {
        anchor: a,
        region: null,
        coord: { x, y: UNKNOWN_COORDS.y, label: UNKNOWN_COORDS.label, flag: UNKNOWN_COORDS.flag },
      };
    });
  }, [anchors]);

  // Anchors grouped by region, so the map labels a region once however many
  // gateways it holds.
  const regionGroups = useMemo(() => {
    const byKey = new Map();
    anchorCoords.forEach((item) => {
      const key = item.region || "__unknown__";
      if (!byKey.has(key)) {
        const meta = item.region ? REGION_COORDS[item.region] : null;
        byKey.set(key, { key, region: item.region, label: meta?.label || UNKNOWN_COORDS.label, items: [] });
      }
      byKey.get(key).items.push(item);
    });
    return Array.from(byKey.values());
  }, [anchorCoords]);

  // The gateway in use first, so the one carrying traffic is the first thing
  // read, then the rest in the box's own order, which is also the order a
  // place's gateways are numbered in.
  const tiles = useMemo(
    () => [...anchorCoords].sort((a, b) => (b.anchor.active ? 1 : 0) - (a.anchor.active ? 1 : 0)),
    [anchorCoords]
  );

  const activeItem = anchorCoords.find((a) => a.anchor.active);

  // Last measured figures per gateway, so a tile can show what it actually
  // costs rather than only where it is.
  const measured = useMemo(() => {
    const by = {};
    (selection?.last?.anchors || []).forEach((a) => { by[a.host] = a; });
    return by;
  }, [selection]);
  const bestHost = selection?.last?.best || "";
  const checkedAt = selection?.last?.at || null;

  // ── Render: no node ──

  if (!selectedNode) {
    return (
      <div className="cloud-panel-v2">
        <div className="cloud-empty">
          <FiGlobe size={48} strokeWidth={1} />
          <h3>Cloud Gateway</h3>
          <p>Select an edge node to view and manage cloud gateways.</p>
        </div>
      </div>
    );
  }

  // ── Render: loading ──

  if (loading && anchors.length === 0) {
    return (
      <div className="cloud-panel-v2">
        <div className="cloud-empty">
          <FiRefreshCw size={28} className="cloud-spin" />
          <p>Loading cloud gateways for {selectedNode}…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cloud-panel-v2" ref={containerRef}>
      {/* ── Toast ── */}
      {switchResult?.ok && (
        <div className="cloud-toast-v2 cloud-toast-v2--ok">
          <FiCheck size={14} /> Switched to <strong>{switchResult.name}</strong>
        </div>
      )}
      {switchResult && !switchResult.ok && (
        <div className="cloud-toast-v2 cloud-toast-v2--err" role="alert">
          <FiAlertCircle size={14} /> {switchResult.message}
        </div>
      )}
      {error && (
        <div className="cloud-toast-v2 cloud-toast-v2--err">
          <FiAlertCircle size={14} /> {error}
        </div>
      )}

      {/* ── Automatic selection ── */}
      {selSupported && selection && (
        <SelectionBar
          selection={selection}
          busy={selBusy}
          nameOf={nameOf}
          onMode={(mode) => updateSelection({ mode })}
          onInterval={(sec) => updateSelection({ interval_sec: sec })}
          onMargin={(pts) => updateSelection({ margin: pts })}
          onRunNow={runSelectionNow}
        />
      )}

      {/* ── Map section ── */}
      <div className="cloud-map-wrap">
        <div className="cloud-map-header">
          <div className="cloud-map-header-left">
            <FiGlobe size={18} />
            <h3>Gateway Map</h3>
          </div>
          <div className="cloud-map-legend">
            <span className="cloud-legend-item cloud-legend-item--active">
              <span className="cloud-legend-dot cloud-legend-dot--active" /> In use
            </span>
            <span className="cloud-legend-item">
              <span className="cloud-legend-dot cloud-legend-dot--standby" /> Not in use
            </span>
            {lastFetch && (
              <span className="cloud-map-ts">
                Updated {lastFetch.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        <svg
          className="cloud-map-svg"
          viewBox="0 0 900 460"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Grid */}
          <defs>
            <pattern id="cloud-grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M50,0 L0,0 0,50" fill="none" stroke="currentColor" strokeWidth="0.3" opacity="0.15" />
            </pattern>
          </defs>
          <rect width="900" height="460" fill="url(#cloud-grid)" rx="12" />

          {/* Continents */}
          <WorldMapPaths />

          {/* Connection lines from each anchor to active */}
          {activeItem && anchorCoords.map((item) => {
            if (item.anchor.active) return null;
            return (
              <ConnectionLine
                key={`line-${item.anchor.host}`}
                from={item.coord}
                to={activeItem.coord}
                isActive={false}
              />
            );
          })}

          {/* Pins */}
          {anchorCoords.map((item) => (
            <AnchorPin
              key={item.anchor.host}
              anchor={item.anchor}
              coord={item.coord}
              isActive={item.anchor.active}
              isSwitching={switching && switchTarget === item.anchor.host}
              onClick={handleSwitch}
              onHover={setHovered}
              onLeave={() => setHovered(null)}
            />
          ))}

          {/* Region labels — one per region, not one per anchor, or a
              clustered region draws the same text on top of itself once per
              member and renders as a smudge. */}
          {regionGroups.map((g) => {
            const anchored = g.region ? REGION_COORDS[g.region] : g.items[0].coord;
            const hasActive = g.items.some((i) => i.anchor.active);
            return (
              <text
                key={`label-${g.key}`}
                x={anchored.x}
                y={anchored.y + (hasActive ? 26 : 21)}
                textAnchor="middle"
                className="cloud-map-label"
                fill={hasActive ? "#4F46E5" : "#64748B"}
                fontWeight={hasActive ? 700 : 500}
              >
                {g.label}{g.items.length > 1 ? ` (${g.items.length})` : ""}
              </text>
            );
          })}

          {/* Tooltip */}
          {hovered && (() => {
            const item = anchorCoords.find((a) => a.anchor.host === hovered.host);
            if (!item) return null;
            return (
              <MapTooltip
                anchor={item.anchor}
                name={names[item.anchor.host]}
                coord={item.coord}
                region={item.region}
                measure={measured[item.anchor.host]}
              />
            );
          })()}
        </svg>
      </div>

      {/* ── One tile per gateway, below the map ── */}
      <div className="cloud-gw-grid" role="list" aria-label="Gateways">
        {tiles.map((item) => (
          <GatewayTile
            key={item.anchor.host}
            anchor={item.anchor}
            name={names[item.anchor.host]}
            region={item.region}
            result={measured[item.anchor.host]}
            checkedAt={checkedAt}
            best={item.anchor.host === bestHost && !item.anchor.active}
            switching={switching ? switchTarget : null}
            onSwitch={handleSwitch}
          />
        ))}
      </div>

      {anchors.length === 0 && !loading && (
        <div className="cloud-empty cloud-empty--small">
          <FiMapPin size={24} />
          <p>This box has no cloud gateway set up. Its gateways are set when the box is provisioned.</p>
        </div>
      )}
    </div>
  );
}
